// F10.3 — RTT-based signal strength probe.
//
// We approximate "signal strength" from the WebRTC ICE connectivity-check
// RTT. The nominated candidate-pair's `currentRoundTripTime` (seconds) is
// updated continuously by the browser once the connection is established;
// reading it via `pc.getStats()` is cheap.

// Bar-bucket thresholds from F10.3 acceptance criteria:
//   <  25ms → 4 (LAN-grade)
//   <  75ms → 3
//   < 200ms → 2
//   ≥ 200ms → 1
//   null    → null (no connection yet — UI renders all-grey)
const THRESHOLDS = [
  { lt: 25, bars: 4 },
  { lt: 75, bars: 3 },
  { lt: 200, bars: 2 },
];

/**
 * Probe round-trip time on an established RTCPeerConnection.
 *
 * Returns the RTT of the nominated candidate-pair in milliseconds, or
 * `null` if no nominated pair was found (typically because the connection
 * hasn't completed ICE yet).
 *
 * @param {RTCPeerConnection} pc
 * @returns {Promise<{ rttMs: number | null }>}
 */
export async function probeRtt(pc) {
  if (!pc || typeof pc.getStats !== 'function') {
    return { rttMs: null };
  }

  let report;
  try {
    report = await pc.getStats();
  } catch {
    return { rttMs: null };
  }
  if (!report) return { rttMs: null };

  // RTCStatsReport behaves like a Map. We look for the nominated
  // candidate-pair (state === 'succeeded' && nominated === true) and pull
  // its `currentRoundTripTime` (seconds, per the W3C spec).
  let best = null;
  const consider = (entry) => {
    if (!entry || entry.type !== 'candidate-pair') return;
    if (entry.nominated !== true) return;
    if (entry.state !== 'succeeded') return;
    const rttSec = entry.currentRoundTripTime;
    if (typeof rttSec !== 'number' || !Number.isFinite(rttSec) || rttSec < 0) {
      return;
    }
    // Prefer the most recently updated nominated pair if multiple exist
    // (rare — happens during ICE restart).
    if (best == null || (entry.timestamp ?? 0) > (best.timestamp ?? 0)) {
      best = entry;
    }
  };

  // The W3C surface is iterable (Map-like). Tolerate plain object reports
  // from tests by falling back to Object.values.
  if (typeof report.forEach === 'function') {
    report.forEach(consider);
  } else if (typeof report === 'object') {
    for (const key of Object.keys(report)) consider(report[key]);
  }

  if (!best) return { rttMs: null };
  return { rttMs: best.currentRoundTripTime * 1000 };
}

/**
 * Map an RTT (ms) into the 4-bar signal display.
 *
 * @param {number | null} rttMs
 * @returns {1 | 2 | 3 | 4 | null}
 */
export function rttToBars(rttMs) {
  if (rttMs == null || !Number.isFinite(rttMs) || rttMs < 0) return null;
  for (const { lt, bars } of THRESHOLDS) {
    if (rttMs < lt) return bars;
  }
  return 1;
}
